import { useMutation } from '@tanstack/react-query';
import { useAccount, useConfig } from 'wagmi';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { createOrderSubmitBody } from '../lib/createOrderSubmitBody';
import { createFullMarketClose } from '../lib/orderHelpers';
import { assertRequiredData } from '../lib/typeGuards';
import { useDataForSigning } from './useDataForSigning';
import { usePositions } from './usePositions';
import { useProducts } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { useWalletStorage } from './useWalletStorage';

export const useCloseAllPositionsMutation = () => {
  const { getSubaccountData } = useWalletStorage();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { address } = useAccount();
  const { signedTypedDataTypes, domain } = useDataForSigning();
  const subaccountData = getSubaccountData(address, selectedSubaccount?.id);
  const config = useConfig();
  const { data: products = [] } = useProducts();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });

  return useMutation({
    mutationKey: ['closeAllPositions'],
    mutationFn: async () => {
      if (!selectedSubaccount || !address) {
        throw new Error('Missing subaccount or wallet connection');
      }

      if (!positions || positions.length === 0) {
        return { successfulPositionIds: [], failedPositionIds: [] };
      }

      const closePromises = positions.map(async (position) => {
        const product = products.find((p) => p.id === position.productId);

        if (!product) {
          return { success: false, productId: position.productId };
        }

        try {
          const params = {
            selectedSubaccount,
            subaccountData,
            partialOrderData: createFullMarketClose(position),
            product,
            connectedAddress: address,
            domain,
            signedTypedDataTypes,
            config,
          } as const;

          assertRequiredData(params);

          await fetchClient
            .POST('/v1/order/submit', { body: await createOrderSubmitBody(params) })
            .then(dataOrThrow);

          return { success: true, productId: position.productId };
        } catch (error) {
          console.error(`Failed to close ${product.displayTicker} position:`, error);
          return { success: false, productId: position.productId };
        }
      });

      const results = await Promise.all(closePromises);

      const successResults = results.filter((result) => result.success);
      const failedResults = results.filter((result) => !result.success);

      return {
        successfulPositionIds: successResults.map((result) => result.productId),
        failedPositionIds: failedResults.map((result) => result.productId),
      };
    },
  });
};
